import {
  formatRecallAnswerAsText,
  isRecallAnswer,
  isSkippedAnswer,
  normalizeAnswerValue,
  toDisplayString,
} from "../../organisms/PatientDetailsV2/CoachAssessment/QuestionField";
import { isAnswered } from "../../organisms/PatientDetailsV2/SharedProfile/showIf";

export type FormattedAnswer =
  | { kind: "answered"; lines: string[] }
  | { kind: "skipped" }
  | { kind: "empty" };

/** Option id / label / emoji → the label the patient saw. */
const optionText = (question: any, value: unknown): string => {
  const text = toDisplayString(value);
  const hit = (question?.options || []).find((option: any) =>
    [option?.id, option?.label, option?.emoji].some(
      (candidate) => candidate != null && String(candidate) === text,
    ),
  );
  return hit ? String(hit.label ?? hit.emoji ?? hit.id) : text;
};

const withUnit = (question: any, text: string) =>
  question?.unit && text && !Number.isNaN(Number(text))
    ? `${text} ${question.unit}`
    : text;

/** Same meal-recall text as the editor's diary view, one line per slot. */
const recallLines = (value: unknown): string[] =>
  isRecallAnswer(value)
    ? formatRecallAnswerAsText(value).split("\n")
    : [toDisplayString(value)];

const likertText = (value: unknown, scales: any): string => {
  const scale = Array.isArray(scales?.default) ? scales.default : [];
  const hit = scale.find(
    (s: any) => typeof s === "object" && Number(s?.v) === Number(value),
  );
  return hit?.short ? `${value} – ${hit.short}` : String(value);
};

/** Mirrors the editor's toggle: custom yes/no labels win over plain Yes/No. */
const toggleText = (question: any, value: unknown): string => {
  const text = String(value).toLowerCase();
  if (text === "yes" || text === "true") return question?.yes?.label || "Yes";
  if (text === "no" || text === "false") return question?.no?.label || "No";
  return String(value);
};

/** Read-only display of one stored answer, matched to its question config. */
export const formatAnswer = (
  question: any,
  raw: unknown,
  scales?: any,
): FormattedAnswer => {
  if (isSkippedAnswer(raw)) return { kind: "skipped" };
  // A toggle saved as boolean `false` is a real "No", not a blank.
  const isToggleNo = question?.type === "toggle" && raw === false;
  if (!isToggleNo && !isAnswered(raw)) return { kind: "empty" };

  const value = normalizeAnswerValue(raw);
  let lines: string[];
  switch (question?.type) {
    case "recall":
      lines = recallLines(value);
      break;
    case "likert":
      lines = [likertText(value, scales)];
      break;
    case "toggle":
      lines = [toggleText(question, value)];
      break;
    default:
      lines = [
        Array.isArray(value)
          ? value
              .map((item) => optionText(question, item))
              .filter(Boolean)
              .join(", ")
          : withUnit(question, optionText(question, value)),
      ];
  }
  lines = lines.filter(Boolean);
  return lines.length ? { kind: "answered", lines } : { kind: "empty" };
};
